import { Spacing } from '@persona-id/design-tokens';
import { TrashIcon } from '@persona-id/design-tokens/icons';
import produce from 'immer';
import { RulesLogic } from 'json-logic-js';
import { get, isEqual, set } from 'lodash/fp';
import { ComponentProps, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import { validateNonEmptyRule } from 'dashboard/apps/DashboardLayoutEditor/CaseConditionalLogicEditor/utils';
import useIsITEInternal from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsITEInternal';
import { TOOLBAR_HEIGHT } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditorToolbar';
import { FLE_TOOLBAR_ID } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditorToolbar/InquiryTemplateEditorToolbar';
import EditOrRulesBlock from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/blocks/EditOrRulesBlock';
import { LOGIC_RULE_INPUT_ID } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/blocks/FieldOrValueInput';
import { DEFAULT_LOGIC_RULE_BLOCK } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/constants';
import { ComponentJsonLogicRule } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/types';
import {
  formatJSONLogicRule,
  getOperatorsMap,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/ComponentJsonLogicEditor/utils';
import { SCREEN_EDITOR_CONTROL_PANEL_ID } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenEditor/InquiryTemplateScreenEditor';
import TranslateAllButton from 'dashboard/apps/InquiryTemplateEditor/TranslateAllButton';
import {
  InputsAccordion,
  isEmptyString,
  TranslationInputGroup,
} from 'dashboard/apps/InquiryTemplateEditor/TranslationInputs/TranslationInputs';
import Divider from 'dashboard/components/Divider';
import CodeEditor from 'dashboard/components-lib/CodeEditor';
import MaximizablePanel from 'dashboard/components-lib/MaximizablePanel';
import PositionedPanel from 'dashboard/components-lib/MaximizablePanel/PositionedPanel';
import { FULL_SCREEN_HEADER_HEIGHT_XS } from 'dashboard/layouts/ShowViewLayoutV2';
import useTranslations from 'dashboard/lib/hooks/useTranslations';
import { ConditionalRule, LogicBlockOrV2, LogicBlockV2 } from 'dashboard/lib/workflows';
import { INQUIRY_TEMPLATE_EDITOR_SHOW_VIEW_ID } from 'dashboard/views/DashboardInquiryTemplatesShowView/DashboardInquiryTemplatesShowView';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Heading } from 'design-system/components/Text';
import ToggleSlider from 'design-system/components/ToggleSlider';
import { INQUIRY_LANGUAGES_ENGLISH, InquiryIso6391LanguageCode } from 'lib/constants/locales';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import useToggle from 'lib/hooks/useToggle';
import { Translation, UiComponentConfigRule } from 'lib/interfaces/ui-components';
import { JsonSchema } from 'lib/json-schema';

const PREFIX_RULES_TEXT = 'When';

/**
 * Recursively checks after deleting the rule if the parent rule is empty
 * and returns the updated rule and path to update
 *
 * Sample initial paths:
 * non-nested: ['or', '0', 'and']
 * nested:     ['or', '0', 'and', '1', 'or', '2', 'and']
 */
const getDeleteUpdatedRuleAndPath = (
  rule: LogicBlockOrV2,
  path: string[],
  index: number,
): { updatedRule: LogicBlockV2[]; pathToUpdate: string[] } => {
  const updatedRule = (get(path, rule) as LogicBlockV2[]).filter((_, i) => i !== index);
  if (updatedRule.length === 0) {
    if (isEqual(['or'], path)) {
      // terminal case is all rules have been deleted - reset to default empty block
      return { updatedRule: DEFAULT_LOGIC_RULE_BLOCK.rules.or, pathToUpdate: path };
    } else {
      return getDeleteUpdatedRuleAndPath(
        rule,
        path.slice(0, path.length - 2),
        parseInt(path[path.length - 2]),
      );
    }
  } else {
    return { updatedRule, pathToUpdate: path };
  }
};

const getValidationWithUpdatedTranslations = (
  validation: UiComponentConfigRule,
  newTranslations: Translation[],
): UiComponentConfigRule =>
  produce(validation, (draftValidation) => {
    const newTranslationLocales = newTranslations.map(({ locale }) => locale);
    const updatedTranslations = (draftValidation?.['message-translations'] ?? [])
      .filter(({ locale }) => !newTranslationLocales.includes(locale))
      .concat(newTranslations);
    draftValidation['message-translations'] = updatedTranslations;
  });

const StyledDivider = styled(Divider)`
  margin-top: 0;
  margin-bottom: ${Spacing[3]};
`;

interface ValidationEditorPanelProps {
  validation: UiComponentConfigRule;
  componentName: string;
  currentLocale: InquiryIso6391LanguageCode;
  enabledLocales: InquiryIso6391LanguageCode[];
  jsonSchema: JsonSchema;
  outsideExceptionalIdPrefixes?: ComponentProps<
    typeof PositionedPanel
  >['outsideExceptionalIdPrefixes'];
  editable: boolean;
  onOutsideClick: () => void;
  updateValidation: (validation: UiComponentConfigRule) => void;
  deleteValidation: () => void;
}

const ValidationEditorPanel = ({
  validation,
  componentName,
  currentLocale,
  enabledLocales,
  jsonSchema,
  outsideExceptionalIdPrefixes,
  editable,
  onOutsideClick,
  updateValidation,
  deleteValidation,
}: ValidationEditorPanelProps) => {
  const isITEInternal = useIsITEInternal();
  const enableScreenEditorLogicCodeEditor = useFeatureFlag(
    'dashboard:ite-enable-screen-editor-logic-code-editor',
  );
  const [updatedValidation, setUpdatedValidation] = useState<UiComponentConfigRule>(validation);
  const [logicCodeEditorOpen, { toggle: toggleJSONLogicCodeEditor }] = useToggle(false);
  const validationRules = formatJSONLogicRule(
    validation.logic as ComponentJsonLogicRule,
    `form.${componentName}`,
  ).rules;

  useEffect(() => {
    setUpdatedValidation(validation);
  }, [validation]);

  const updateConditionalRule = (updatedLogic: LogicBlockOrV2) => {
    const updatedValidation: UiComponentConfigRule = {
      ...validation,
      logic: updatedLogic as RulesLogic,
    };
    setUpdatedValidation(updatedValidation);
    updateValidation(updatedValidation);
  };

  const updateRule = (path: string[]) => (rule: ConditionalRule) => {
    const newConditional = set(['or', ...path], rule, validationRules);
    updateConditionalRule(newConditional);
  };

  const deleteRule = (path: string[], index: number) => {
    const { updatedRule, pathToUpdate } = getDeleteUpdatedRuleAndPath(
      validationRules,
      ['or', ...path],
      index,
    );
    const newConditional = set(pathToUpdate, updatedRule, validationRules);
    updateConditionalRule(newConditional);
  };

  const addAndRule = (path: string[], nestedGroup: boolean = false) => {
    const newRule = nestedGroup ? { or: [{ and: [{}] }] } : {};
    const andRules = get(['or', ...path], validationRules);
    const newConditional = set(['or', ...path], [...andRules, newRule], validationRules);
    updateConditionalRule(newConditional);
  };

  const addOrRule = (path: string[] = ['or']) => {
    const orRules = get(path, validationRules);
    const newConditional = set(path, orRules.concat({ and: [{}] }), validationRules);
    updateConditionalRule(newConditional);
  };

  const sourceErrorMessageText = updatedValidation['message-translations']?.find(
    ({ locale }) => locale === currentLocale,
  )?.value;
  const hasMissingSourceText = isEmptyString(sourceErrorMessageText);

  const { loading, requestTranslations } = useTranslations();

  const handleTranslateAll = async () => {
    if (sourceErrorMessageText != null && !Array.isArray(sourceErrorMessageText)) {
      const newTranslations = await requestTranslations([
        [sourceErrorMessageText, enabledLocales.filter((locale) => locale !== currentLocale)],
      ]);
      if (newTranslations[sourceErrorMessageText]) {
        const newValidationTranslations = Object.entries(
          newTranslations[sourceErrorMessageText],
        ).flatMap(([locale, value]) => ({ locale, value })) as Translation[];
        updateValidation(
          getValidationWithUpdatedTranslations(updatedValidation, newValidationTranslations),
        );
      }
    }
  };

  const operatorsMap = getOperatorsMap('server');

  return createPortal(
    <MaximizablePanel
      title={
        <Heading size='sm' as='h2'>
          Validation Rule
        </Heading>
      }
      subtitle='Display the error message below when the configured conditions are not met'
      offsetElementId={SCREEN_EDITOR_CONTROL_PANEL_ID}
      fullscreenTop={TOOLBAR_HEIGHT + FULL_SCREEN_HEADER_HEIGHT_XS}
      outsideExceptionalIdPrefixes={[
        ...(outsideExceptionalIdPrefixes || []),
        FLE_TOOLBAR_ID,
        LOGIC_RULE_INPUT_ID,
      ]}
      rightButton={
        <HStack equal>
          {(isITEInternal || enableScreenEditorLogicCodeEditor) && (
            <ToggleSlider
              titleOn='Code editor open'
              titleOff='Code editor closed'
              labelOn='Close code editor'
              labelOff='Open code editor'
              value={logicCodeEditorOpen}
              onChange={toggleJSONLogicCodeEditor}
            />
          )}
          <Button
            width='icon'
            variant='inverse'
            aria-label='Delete logic rule'
            id='component-conditional-logic-panel__delete-button'
            disabled={!editable}
            onClick={deleteValidation}
          >
            <TrashIcon />
          </Button>
        </HStack>
      }
      onOutsideClickAndEscPress={onOutsideClick}
    >
      <StyledDivider full />
      {logicCodeEditorOpen ? (
        <CodeEditor
          language='json'
          code={JSON.stringify(validationRules, null, 2)}
          completionSources={{}}
          readOnly={!editable}
          onChange={(updatedConditional: string) =>
            updateValidation({ ...validation, logic: JSON.parse(updatedConditional) })
          }
        />
      ) : (
        <>
          <TranslationInputGroup
            locale={currentLocale}
            label='Error message'
            inputProps={{
              size: 'md',
              onBlur: () => {
                updateValidation(updatedValidation);
              },
              onChange: (e) => {
                setUpdatedValidation(
                  getValidationWithUpdatedTranslations(updatedValidation, [
                    { locale: currentLocale, value: e.target.value },
                  ]),
                );
              },
              value: updatedValidation['message-translations']?.find(
                ({ locale }) => locale === currentLocale,
              )?.value,
            }}
            translationSourceText={sourceErrorMessageText}
            disabled={!editable}
            withTranslateButton={false}
          />
          <Spacer height={2} />
          <EditOrRulesBlock
            keyPrefix='component-conditional__'
            rules={validationRules}
            jsonSchema={jsonSchema}
            prefixText={PREFIX_RULES_TEXT}
            operatorsMap={operatorsMap}
            editable={editable}
            validateRule={validateNonEmptyRule}
            updateRule={updateRule}
            deleteRule={deleteRule}
            addAndRule={addAndRule}
            addOrRule={addOrRule}
            enableValidation
          />
          <InputsAccordion
            caretSize='md'
            title={`Translations (${enabledLocales.length - 1})`}
            menuItemBorder={false}
            menuItemHover={false}
            menuItem
          >
            <>
              <TranslateAllButton
                id='validation-error-message__translate-all-button'
                variant='basic'
                hasMissingSourceText={hasMissingSourceText}
                isPending={loading}
                handleTranslateAll={handleTranslateAll}
              />
              {enabledLocales
                .filter((locale) => locale !== currentLocale)
                .map((locale) => {
                  return (
                    <TranslationInputGroup
                      key={locale}
                      locale={locale}
                      label={INQUIRY_LANGUAGES_ENGLISH[locale]}
                      inputProps={{
                        size: 'md',
                        onBlur: () => updateValidation(updatedValidation),
                        onChange: (e) => {
                          setUpdatedValidation(
                            getValidationWithUpdatedTranslations(updatedValidation, [
                              { locale, value: e.target.value },
                            ]),
                          );
                        },
                        placeholder: 'No custom translation',
                        value: updatedValidation['message-translations']?.find(
                          ({ locale: translationLocale }) => translationLocale === locale,
                        )?.value,
                      }}
                      translationSourceText={sourceErrorMessageText}
                      disabled={!editable}
                      onTranslationsRequest={(translation: string) =>
                        updateValidation(
                          getValidationWithUpdatedTranslations(updatedValidation, [
                            { locale, value: translation },
                          ]),
                        )
                      }
                    />
                  );
                })}
            </>
          </InputsAccordion>
        </>
      )}
    </MaximizablePanel>,
    document.getElementById(INQUIRY_TEMPLATE_EDITOR_SHOW_VIEW_ID) as Element,
  );
};

export default ValidationEditorPanel;
